import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, CheckCircle, Gift, Users, Sparkles, Target, Star, Mail, Phone, Globe } from 'lucide-react';

const WaitlistPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [waitlistStats, setWaitlistStats] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const recommendedTrack = searchParams.get('track') || 'apostolic_prophetic';
  const sessionId = searchParams.get('session');

  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    country: '',
    timezone: '',
    selected_track: recommendedTrack,
    recommended_track: recommendedTrack,
    assessment_session_id: sessionId,
    selected_resources: [],
    current_ministry: ''
  });

  useEffect(() => {
    fetchWaitlistStats();
  }, []);

  const fetchWaitlistStats = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/waitlist/status`);
      if (response.ok) {
        const data = await response.json();
        setWaitlistStats(data);
      }
    } catch (error) {
      console.error('Error fetching waitlist stats:', error);
    }
  };

  const trackOptions = [
    {
      value: 'apostolic_prophetic',
      name: 'Apostolic & Prophetic Leadership',
      icon: Target,
      color: 'purple',
      description: 'For those called to teach, prophesy, and lead in ministry'
    },
    {
      value: 'minstrel_worship',
      name: 'Minstrel & Worship Leadership',
      icon: Star,
      color: 'orange',
      description: 'For worship leaders and musicians called to priestly ministry'
    }
  ];

  const freeResources = [
    { id: 'prophetic_foundations', name: '📖 Ebook: Foundations of the Prophetic', icon: '📖' },
    { id: 'apostolic_authority', name: '📕 Ebook: Apostolic Authority Unveiled', icon: '📕' },
    { id: 'hearing_god', name: '🎥 Video: 5 Keys to Hearing God Clearly', icon: '🎥' },
    { id: 'spiritual_habits', name: '📋 Guide: Building Godly Spiritual Habits', icon: '📋' }
  ];

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleResourceToggle = (resourceId) => {
    const currentResources = formData.selected_resources;
    if (currentResources.includes(resourceId)) {
      setFormData({
        ...formData,
        selected_resources: currentResources.filter(id => id !== resourceId)
      });
    } else if (currentResources.length < 2) {
      setFormData({
        ...formData,
        selected_resources: [...currentResources, resourceId]
      });
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/waitlist/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        setIsComplete(true);
        setWaitlistStats({
          total_waitlist: data.total_waitlist,
          position: data.position,
          estimated_spots_remaining: data.estimated_spots_remaining
        });
      } else {
        alert('Error registering for waitlist. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to register. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return formData.selected_track;
    if (step === 2) return formData.email && formData.full_name;
    if (step === 3) return formData.selected_resources.length === 2;
    return false;
  };

  if (isComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-orange-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 md:p-12 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            🎉 You're on the Waitlist!
          </h1>

          <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-8">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-600">Your Position</p>
                <p className="text-3xl font-bold text-purple-600">#{waitlistStats?.position}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Waitlist</p>
                <p className="text-3xl font-bold text-orange-600">{waitlistStats?.total_waitlist}</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              {waitlistStats?.estimated_spots_remaining} spots remaining for January 2026 cohort
            </p>
          </div>

          <div className="text-left space-y-6 mb-8">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">📬 What Happens Next:</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Immediate:</p>
                    <p className="text-gray-600 text-sm">Check your email for your free resources and welcome message from Pastor Obed</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Between Now & January:</p>
                    <p className="text-gray-600 text-sm">Weekly encouragement emails, exclusive pre-launch content, and early community access</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">December 2025:</p>
                    <p className="text-gray-600 text-sm">Priority enrollment opens with special founding member pricing</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gradient-to-r from-purple-600 to-orange-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-orange-700 transition-all shadow-lg"
            >
              Explore the Academy
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gray-100 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:bg-gray-200 transition-all"
            >
              Return to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-orange-600 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Calendar className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            January 2026 Cohort - Now Enrolling
          </h1>
          <p className="text-xl text-white/90">
            Reserve your spot today and receive exclusive pre-launch access
          </p>

          {waitlistStats && (
            <div className="mt-6 inline-block bg-white/20 backdrop-blur-sm px-8 py-4 rounded-xl">
              <p className="text-sm mb-1">Waitlist Progress</p>
              <div className="flex items-center space-x-4">
                <Users className="w-5 h-5" />
                <div className="w-48 bg-white/30 rounded-full h-2">
                  <div
                    className="bg-white h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (waitlistStats.total_waitlist / 100) * 100)}%` }}
                  />
                </div>
                <span className="font-bold">{waitlistStats.total_waitlist}/100</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Progress Steps */}
        <div className="flex items-center justify-center space-x-4 mb-8">
          {[1, 2, 3, 4].map((stepNum) => (
            <React.Fragment key={stepNum}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= stepNum
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {stepNum}
              </div>
              {stepNum < 4 && (
                <div className={`w-12 h-1 ${
                  step > stepNum ? 'bg-purple-600' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* Step 1: Confirm Track */}
          {step === 1 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirm Your Track</h2>
              <p className="text-gray-600 mb-6">Based on your assessment, we recommend this track. Feel free to change if needed.</p>

              <div className="space-y-4">
                {trackOptions.map((track) => {
                  const TrackIcon = track.icon;
                  const isSelected = formData.selected_track === track.value;

                  return (
                    <button
                      key={track.value}
                      onClick={() => handleInputChange('selected_track', track.value)}
                      className={`w-full text-left p-6 rounded-xl border-2 transition-all ${
                        isSelected
                          ? `border-${track.color}-600 bg-${track.color}-50`
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`p-3 bg-${track.color}-100 rounded-lg`}>
                          <TrackIcon className={`w-6 h-6 text-${track.color}-600`} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-lg text-gray-900 mb-1">{track.name}</h3>
                          <p className="text-gray-600 text-sm">{track.description}</p>
                          {formData.recommended_track === track.value && (
                            <span className="inline-block mt-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                              ✓ Recommended for You
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Profile Info */}
          {step === 2 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Complete Your Profile</h2>
              <p className="text-gray-600 mb-6">We'll use this information to personalize your experience.</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.full_name}
                    onChange={(e) => handleInputChange('full_name', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number (Optional)
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="+44 123 456 7890"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="United Kingdom"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Timezone (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.timezone}
                      onChange={(e) => handleInputChange('timezone', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="GMT / UTC+0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Ministry Involvement (Optional)
                  </label>
                  <textarea
                    value={formData.current_ministry}
                    onChange={(e) => handleInputChange('current_ministry', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows="3"
                    placeholder="e.g., Worship team member at local church, Small group leader, etc."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Choose Bonuses */}
          {step === 3 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Free Resources</h2>
              <p className="text-gray-600 mb-6">Select 2 resources to receive immediately after registration.</p>

              <div className="space-y-3">
                {freeResources.map((resource) => {
                  const isSelected = formData.selected_resources.includes(resource.id);
                  const canSelect = formData.selected_resources.length < 2 || isSelected;

                  return (
                    <button
                      key={resource.id}
                      onClick={() => canSelect && handleResourceToggle(resource.id)}
                      disabled={!canSelect}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-purple-600 bg-purple-50'
                          : canSelect
                          ? 'border-gray-200 hover:border-gray-300'
                          : 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{resource.icon}</span>
                          <span className={`font-medium ${
                            isSelected ? 'text-purple-900' : 'text-gray-700'
                          }`}>
                            {resource.name}
                          </span>
                        </div>
                        {isSelected && (
                          <CheckCircle className="w-5 h-5 text-purple-600" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <p className="mt-4 text-sm text-gray-500 text-center">
                {formData.selected_resources.length}/2 selected
              </p>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Review & Confirm</h2>
              <p className="text-gray-600 mb-6">Please review your information before submitting.</p>

              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-sm text-gray-600 mb-1">Selected Track</p>
                  <p className="font-semibold text-gray-900">
                    {trackOptions.find(t => t.value === formData.selected_track)?.name}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-sm text-gray-600 mb-1">Contact Information</p>
                  <p className="font-semibold text-gray-900">{formData.full_name}</p>
                  <p className="text-gray-700">{formData.email}</p>
                  {formData.phone && <p className="text-gray-700">{formData.phone}</p>}
                </div>

                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-sm text-gray-600 mb-2">Selected Free Resources</p>
                  {formData.selected_resources.map(resourceId => {
                    const resource = freeResources.find(r => r.id === resourceId);
                    return (
                      <p key={resourceId} className="text-gray-700 flex items-center space-x-2">
                        <span>{resource?.icon}</span>
                        <span>{resource?.name}</span>
                      </p>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="mt-8 flex justify-between">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-all"
              >
                Previous
              </button>
            )}

            {step < 4 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className={`ml-auto px-8 py-3 rounded-xl font-semibold transition-all ${
                  canProceed()
                    ? 'bg-gradient-to-r from-purple-600 to-orange-600 text-white hover:from-purple-700 hover:to-orange-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Continue
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="ml-auto px-8 py-3 bg-gradient-to-r from-purple-600 to-orange-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-orange-700 transition-all disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Confirm & Join Waitlist'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitlistPage;